import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import useInstanceStore from '../stores/instanceStore';
import progressTracker from '../services/progressTracker';
import { API_BASE_URL } from '../services/api';

const ChapterContext = createContext();

export const useChapter = () => {
  const context = useContext(ChapterContext);
  if (!context) {
    throw new Error('useChapter must be used within a ChapterProvider');
  }
  return context;
};

export const ChapterProvider = ({ children }) => {
  const { selectedInstanceId } = useInstanceStore();
  const [currentChapter, setCurrentChapter] = useState(null);
  const [chapterProgress, setChapterProgress] = useState({});
  const [lastViewedPosition, setLastViewedPosition] = useState({});
  const [backendProgress, setBackendProgress] = useState({});
  const [isProgressLoading, setIsProgressLoading] = useState(false);

  // Load chapter state when instance changes
  useEffect(() => {
    if (selectedInstanceId) {
      // Load from localStorage as fallback
      loadLocalProgress();
      
      // Load from backend (primary source)
      loadBackendProgress();
    }
  }, [selectedInstanceId]);

  // Load progress from localStorage (fallback)
  const loadLocalProgress = useCallback(() => {
    if (!selectedInstanceId) return;

    try {
      // Load current chapter
      const savedChapter = localStorage.getItem(`current_chapter_${selectedInstanceId}`);
      if (savedChapter) {
        setCurrentChapter(parseInt(savedChapter));
      }

      // Load chapter progress
      const savedProgress = localStorage.getItem(`chapter_progress_${selectedInstanceId}`);
      if (savedProgress) {
        setChapterProgress(JSON.parse(savedProgress));
      }

      // Load last viewed positions
      const savedPositions = localStorage.getItem(`chapter_positions_${selectedInstanceId}`);
      if (savedPositions) {
        setLastViewedPosition(JSON.parse(savedPositions));
      }
    } catch (error) {
      console.error('Failed to load local progress:', error);
    }
  }, [selectedInstanceId]);

  // Load progress from backend
  const loadBackendProgress = useCallback(async () => {
    if (!selectedInstanceId) return;

    setIsProgressLoading(true);
    try {
      const progress = await progressTracker.loadProgressForInstance(selectedInstanceId);
      setBackendProgress(progress);
      
      // Update current chapter from backend data
      if (progress.instance_progress?.current_chapter_number) {
        setCurrentChapter(progress.instance_progress.current_chapter_number);
      }
      
      // Convert backend chapters progress to local format
      if (progress.chapters_progress) {
        const convertedProgress = {};
        progress.chapters_progress.forEach(chapter => {
          convertedProgress[chapter.chapter_number] = {
            viewed_posts: chapter.viewed_content_items,
            total_posts: chapter.total_content_items,
            progress: Math.round(chapter.overall_progress),
            last_updated: chapter.updated_at,
            reading_time_seconds: chapter.total_viewport_time_seconds
          };
        });
        setChapterProgress(convertedProgress);
      }

    } catch (error) {
      console.error('Failed to load backend progress:', error);
    } finally {
      setIsProgressLoading(false);
    }
  }, [selectedInstanceId]);

  // Save chapter state to localStorage when it changes
  useEffect(() => {
    if (selectedInstanceId && currentChapter !== null) {
      localStorage.setItem(`current_chapter_${selectedInstanceId}`, currentChapter.toString());
    }
  }, [selectedInstanceId, currentChapter]);

  useEffect(() => {
    if (selectedInstanceId && Object.keys(chapterProgress).length > 0) {
      localStorage.setItem(`chapter_progress_${selectedInstanceId}`, JSON.stringify(chapterProgress));
    }
  }, [selectedInstanceId, chapterProgress]);

  useEffect(() => {
    if (selectedInstanceId && Object.keys(lastViewedPosition).length > 0) {
      localStorage.setItem(`chapter_positions_${selectedInstanceId}`, JSON.stringify(lastViewedPosition));
    }
  }, [selectedInstanceId, lastViewedPosition]);

  const navigateToChapter = useCallback((chapterNumber, scrollToPosition = null) => {
    setCurrentChapter(chapterNumber);
    
    // Update backend chapter position
    if (selectedInstanceId && scrollToPosition !== null) {
      progressTracker.updateChapterPosition(
        selectedInstanceId, 
        chapterNumber, 
        scrollToPosition,
        null // last_content_id - will be updated by progress tracker
      );
      
      // Also save to localStorage as fallback
      setLastViewedPosition(prev => ({
        ...prev,
        [chapterNumber]: scrollToPosition
      }));
    }
  }, [selectedInstanceId]);

  // Track content element for reading progress
  const trackContentProgress = useCallback((element, contentData) => {
    if (!selectedInstanceId) return;
    
    progressTracker.trackContent(element, {
      ...contentData,
      instance_id: selectedInstanceId
    });
  }, [selectedInstanceId]);

  // Stop tracking content element
  const untrackContentProgress = useCallback((contentId) => {
    progressTracker.untrackContent(contentId);
  }, []);

  const updateChapterProgress = useCallback((chapterNumber, viewedPosts, totalPosts) => {
    const progressPercent = totalPosts > 0 ? Math.round((viewedPosts / totalPosts) * 100) : 0;
    
    setChapterProgress(prev => ({
      ...prev,
      [chapterNumber]: {
        viewed_posts: viewedPosts,
        total_posts: totalPosts,
        progress: progressPercent,
        last_updated: new Date().toISOString()
      }
    }));
  }, []);

  const getChapterProgress = (chapterNumber) => {
    return chapterProgress[chapterNumber] || {
      viewed_posts: 0,
      total_posts: 0,
      progress: 0
    };
  };

  const getLastViewedPosition = (chapterNumber) => {
    return lastViewedPosition[chapterNumber] || null;
  };

  const resetChapterState = () => {
    setCurrentChapter(null);
    setChapterProgress({});
    setLastViewedPosition({});
    
    if (selectedInstanceId) {
      localStorage.removeItem(`current_chapter_${selectedInstanceId}`);
      localStorage.removeItem(`chapter_progress_${selectedInstanceId}`);
      localStorage.removeItem(`chapter_positions_${selectedInstanceId}`);
    }
  };

  const value = {
    currentChapter,
    chapterProgress,
    lastViewedPosition,
    backendProgress,
    isProgressLoading,
    navigateToChapter,
    updateChapterProgress,
    getChapterProgress,
    getLastViewedPosition,
    resetChapterState,
    setCurrentChapter,
    trackContentProgress,
    untrackContentProgress,
    loadBackendProgress
  };

  return (
    <ChapterContext.Provider value={value}>
      {children}
    </ChapterContext.Provider>
  );
};

export default ChapterContext;